import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import React, {
  createContext,
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  useInfinitePaginationContext,
  useListContext,
  useResourceContext,
} from 'react-admin';
import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';
import type { MenuConfigMap } from 'components/Minimenus/MiniMenuSelector';
import { AnyRecord } from 'utility/types/cosmosTypes';
import {
  checkSiteAdminPermission,
  checkSyndicationFullAccessPermission,
  checkSyndicationImportContentPermission,
  checkSyndicationModifyChannelsPermission,
} from 'utility/permissions';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { SessionConfigData } from 'utility/types/dataProvider';

/**
 * Generic type that is compatible with RA list data items.
 * eg: EditorialAssetSearchResultItem | UserAccountSummary | etc...
 *
 * Record id may not always be 'id' field on object
 * and is only specified to comply with RA useListContext types.
 * Use getRecordRealId helper to resolve id for records.
 * React keys are via getReactUniqueId in RequestBuilders.
 */

export type GenericSearchItem = {
  [key: string]: any;
  id: string;
};

/**
 * Some objects use different keys for their primary ids
 * (eg: id, uuid, object_id, etc...).
 *
 * Accepts a record and a resource to try and resolve its true id
 * as some records use different keys eg .uuid, .id, etc... and
 * the react-admin id prop does not always represent api data id
 * for more info see providers/functions determineUniqueId cases.
 *
 * Example Resources:
 *   editorial
 *   user
 *   user/activities
 *   wire/channel/search
 *   savedsearch
 *   ...
 *
 */

export function getRecordRealId(record?: AnyRecord | null, resource?: string): string {
  if (!record) {
    console.warn('[getRecordRealId] Missing record param:', record);
    return '';
  }
  if (!resource) {
    console.warn('[getRecordRealId] Missing resource param:', resource);
    return '';
  }
  switch (resource) {
    // resource records that use .uuid
    case 'user':
    case 'user/account/multisite':
    case 'emailreach/account':
    case 'editorial/templates':
    case 'editorial': {
      const getId = record?.uuid || '';
      if (!record?.uuid) {
        console.warn('[getRecordRealId] Missing ID:', record);
      }
      return getId;
    }
    // resource records that use .id
    case 'core/logs':
    case 'user/activities':
    case 'savedsearch':
    case 'subscription/services':
    case 'broadcast/account':
    case 'insights/dashboard': {
      const getId = record?.id || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing ID:', record);
      }
      return getId;
    }
    case 'pay/transactions': {
      const getId = record?.id || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing ID:', record);
      }
      return getId;
    }
    // resource records that use .name
    case 'experiments': {
      const getId = record?.name || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing ID:', record);
      }
      return getId;
    }
    // Feeds / Syndication specific
    case 'wire/channel':
    case 'wire/channel/search': {
      // In some cases object_id is just a uuid, but in others it
      // is JSON string with an external_id (parsed as object_id?.external_id)
      // however for the purpose of this function, we are only concerned
      // with returning what value is present at the object_id field
      // so it will return the raw value, and from there you can
      // determine if deeper if you to need parse it as json or not
      const objectId = record?.object_id || record?.id || '';
      if (!objectId) {
        console.warn('[getRecordRealId] Missing ID:', record);
      }
      return objectId;
    }
    // Handle EmailReach contacts resource
    case 'emailreach/contact': {
      const getId = record?.contact_id || '';
      if (!getId) {
        console.warn(
          '[getRecordRealId] Missing contact_id for EmailReach contact:',
          record,
        );
      }
      return getId;
    }
    case 'emailreach/contactlist': {
      const getId = record?.list_id || '';
      if (!getId) {
        console.warn(
          '[getRecordRealId] Missing list_id for EmailReach contact list:',
          record,
        );
      }
      return getId;
    }
    case 'emailreach/messages': {
      const getId = record?.uuid || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing uuid for EmailReach message:', record);
      }
      return getId;
    }
    case 'emailreach/campaign': {
      // EmailReach Campaigns use uuid as unique identifier
      const getId = record?.uuid || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing ID for EmailReach campaign:', record);
      }
      return getId;
    }
    case 'broadcast/message': {
      const getId = record?.message_id || '';
      if (!getId) {
        console.warn(
          '[getRecordRealId] Missing message_id for Broadcast message:',
          record,
        );
      }
      return getId;
    }
    case 'broadcast/channel': {
      const getId = record?.id || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing id for Broadcast channel:', record);
      }
      return getId;
    }
    case DASHBOARD_RESOURCE: {
      const getId = record?.id || '';
      if (!getId) {
        console.warn('[getRecordRealId] Dashboards Missing ID:', record);
      }
      return getId;
    }
    case 'group': {
      const getId = record?.id || '';
      if (!getId) {
        console.warn('[getRecordRealId] Missing id for group:', record);
      }
      return getId;
    }
    default: {
      // Warning and try to resolve something...
      console.warn('[getRecordRealId] Defaulted:', { resource, record });
      const fallback = record?.uuid || record?.id || '';
      return fallback;
    }
  }
}

export function checkSelectionEnabled(resource?: string) {
  if (!resource) {
    return false;
  }
  const enabledResources = ['editorial', 'editorial/templates', DASHBOARD_RESOURCE];
  const isEnabled = enabledResources.includes(resource);
  return isEnabled;
}

export function checkMiniMenuEnabled(
  resource?: string,
  sessionConfig?: SessionConfigData | undefined,
) {
  if (!resource) {
    return false;
  }
  const enabledResources = [
    'editorial',
    'savedsearch',
    'wire/channel',
    'wire/channel/search',
    'broadcast/message',
    'emailreach/campaign',
    'insights/dashboard', // DASHBOARD_RESOURCE
  ];

  // Default to true unless we have a defined resource permissions check
  let showActions = true;
  switch (resource) {
    case 'wire/channel/search': {
      const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
      const { granted: userHasFullWire } =
        checkSyndicationFullAccessPermission(sessionConfig);
      const { granted: userHasImportWire } =
        checkSyndicationImportContentPermission(sessionConfig);

      showActions = Boolean(userIsAdmin || userHasFullWire || userHasImportWire);
      break;
    }

    case 'wire/channel': {
      const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
      const { granted: userHasFullWire } =
        checkSyndicationFullAccessPermission(sessionConfig);
      const { granted: userHasModChannelsWire } =
        checkSyndicationModifyChannelsPermission(sessionConfig);

      showActions = Boolean(userIsAdmin || userHasFullWire || userHasModChannelsWire);
      break;
    }
  }

  const isEnabled = enabledResources.includes(resource) && showActions;
  return isEnabled;
}

// Function to remove duplicate rows (for syndication/wire usage)
export const dedupeRowsById = (
  rows: GenericSearchItem[],
  key: string = 'id',
): GenericSearchItem[] => {
  if (!Array.isArray(rows) || rows?.length === 0) {
    return [];
  }
  const uniqueRowsMap = new Map<string, GenericSearchItem>();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const uniqueKey = row[key];
    if (uniqueKey !== undefined && !uniqueRowsMap.has(uniqueKey)) {
      uniqueRowsMap.set(uniqueKey, row);
    }
  }
  const deduped = Array.from(uniqueRowsMap.values());
  return deduped;
};

const emptyMap = new Map<string, GenericSearchItem>();
const emptyArr: GenericSearchItem[] = [];

export interface SearchItemsContextType<T extends GenericSearchItem = GenericSearchItem> {
  recordMap: Map<string, T>; // map id to record for fast lookups
  cleanData: T[]; // dedupe for "wire/channel/search", etc...
  // Selection handling (batch edit, right click menu, etc)
  hasMiniMenuEnabled: boolean; // Check if mini menu is enabled
  hasSelectionEnabled: boolean; // Check if selection is enabled
  selectedItemIds: GridRowSelectionModel; // for MUI rowSelectionModel string[]
  selectItem: (record?: T) => void; // Select item callback
  selectById: (id?: string) => void; // Select by id callback
  deselectById: (id?: string) => void; // Deselect by id callback
  toggleSelectItem: (record?: T) => void; // Toggle select item callback
  clearSelectedItems: () => void; // Clear selected items callback
  // base setstate/value as needed eg if needing to set state directly
  // in general prefer above methods or use in conjunction with getRecordRealId helper
  // as they will ensure selecting item goes to end of selectedSearchItems array
  setSelectedItems: Dispatch<SetStateAction<T[]>>;
  selectedItems: T[]; // selected items
  lastIndexSelection: T | undefined; // item at last array index of selectedSearchItems
  // exporting from react-admin to centralize contexts
  // adding id to types for react-admin, maybe exists on real data
  data: ReturnType<typeof useListContext<T>>['data'];
  total: ReturnType<typeof useListContext<T>>['total'];
  hasNextPage: ReturnType<typeof useListContext<T>>['hasNextPage'];
  hasPreviousPage: ReturnType<typeof useListContext<T>>['hasPreviousPage'];
  isFetching: ReturnType<typeof useListContext<T>>['isFetching'];
  isLoading: ReturnType<typeof useListContext<T>>['isLoading'];
  page: ReturnType<typeof useListContext<T>>['page'];
  perPage: ReturnType<typeof useListContext<T>>['perPage'];
  refetch: ReturnType<typeof useListContext<T>>['refetch'];
  error: ReturnType<typeof useListContext<T>>['error'];
  // infinite pagination context
  fetchNextPage: ReturnType<typeof useInfinitePaginationContext>['fetchNextPage'];
  fetchPreviousPage: ReturnType<typeof useInfinitePaginationContext>['fetchPreviousPage'];
  isFetchingNextPage: ReturnType<
    typeof useInfinitePaginationContext
  >['isFetchingNextPage'];
  isFetchingPreviousPage: ReturnType<
    typeof useInfinitePaginationContext
  >['isFetchingPreviousPage'];
  infHasNextPage: ReturnType<typeof useInfinitePaginationContext>['hasNextPage'];
  infHasPrevPage: ReturnType<typeof useInfinitePaginationContext>['hasPreviousPage'];
  // Mini menu configuration
  miniMenuConfig?: MenuConfigMap;
  miniMenuHandlers?: {
    onEdit?: (item: any) => void;
    onDuplicate?: (item: any) => void;
    onDelete?: (items: any[]) => void;
  };
}

const SearchItemsContext = createContext<
  SearchItemsContextType<GenericSearchItem> | undefined
>(undefined);

// Hook accepts a generic T type eg `const ctx = useSearchItems<UserAccountSummary>()`
export const useSearchItems = <
  T extends GenericSearchItem = GenericSearchItem,
>(): SearchItemsContextType<T> => {
  const ctx = useContext(
    SearchItemsContext as React.Context<SearchItemsContextType<T> | undefined>,
  );
  if (!ctx) {
    throw new Error('useSearchItems must be used within a SearchItemsProvider');
  }
  return ctx;
};

/**
 * Safe version of useSearchItems that returns null instead of throwing
 * when used outside of SearchItemsProvider. Useful for components
 * that may optionally be used within or outside search views.
 */
export const useSearchItemsSafe = <
  T extends GenericSearchItem = GenericSearchItem,
>(): SearchItemsContextType<T> | null => {
  const ctx = useContext(
    SearchItemsContext as React.Context<SearchItemsContextType<T> | undefined>,
  );
  return ctx ?? null;
};

interface SearchItemsProviderProps {
  children: React.ReactNode;
  miniMenuConfig?: MenuConfigMap;
  miniMenuHandlers?: {
    onEdit?: (item: any) => void;
    onDuplicate?: (item: any) => void;
    onDelete?: (items: any[]) => void;
  };
  enableSelection?: boolean;
  maxSelections?: number;
}

/**
 * SearchItemsProvider
 *
 * custom context that overrides/reexports some react-admin hooks
 * generally for us to be able to define our own handlers or overrides
 * for certain things react-admin ctx has, but we need more control over
 *
 * generally can be placed right below any <InfiniteListBase>
 * and replace most useListContext / useInfinitePaginationContext
 * access instead with useSearchItems() hook
 *
 * context is here for specifically things that cannot go into
 * <SearchContextProvider> due to react-admin hook/provider access order
 * most other things probably will go to the parent <SearchContextProvider>
 *
 * Cleaning of data for syndication/wire usage for dedupes.
 * Handles selected records and util helpers for custom selection logic.
 * Re-exports common react-admin hooks from one spot that is in our control.
 *
 */
export const SearchItemsProvider = memo(
  ({
    children,
    miniMenuConfig,
    miniMenuHandlers,
    enableSelection,
    maxSelections,
  }: SearchItemsProviderProps) => {
    const resource = useResourceContext();
    const { sessionConfig } = useCosmos();

    const {
      data,
      hasNextPage,
      hasPreviousPage,
      isFetching,
      isLoading,
      page,
      perPage,
      refetch,
      error,
      total,
    } = useListContext<GenericSearchItem>();

    const {
      fetchNextPage,
      isFetchingNextPage,
      isFetchingPreviousPage,
      fetchPreviousPage,
      hasNextPage: infHasNextPage,
      hasPreviousPage: infHasPrevPage,
    } = useInfinitePaginationContext();

    // preprocessing data for syndication/wire usage etc
    const cleanData = useMemo(() => {
      if (!data) return [];
      switch (resource) {
        case 'wire/channel/search':
          // deduping only for "wire/channel/search" by react-admin id key
          const cleanedRows = dedupeRowsById(data, 'id');
          return cleanedRows;

        // Dashboards also need dedupe by id.
        case DASHBOARD_RESOURCE:
          return dedupeRowsById(data, 'id');

        default:
          return data;
      }
    }, [data, resource]);

    // guard as syndi/feeds can crash map occasionally, maybe
    // react-admin issue or syndi mutates list before data ready?
    // map here for fast lookups of items by true id
    const recordMap = useMemo(() => {
      if (!cleanData || !Array.isArray(cleanData)) {
        return emptyMap;
      }
      const map = new Map<string, GenericSearchItem>();
      for (const rec of cleanData) {
        const recId = getRecordRealId(rec, resource);
        if (recId) {
          map.set(recId, rec); // if rec has an id add to map
        }
      }
      return map;
    }, [resource, cleanData]);

    // Holds selected objects/rows for custom selection logic
    const [selectedItems, setSelectedItemsState] =
      useState<GenericSearchItem[]>(emptyArr);

    const setSelectedItems = useCallback<Dispatch<SetStateAction<GenericSearchItem[]>>>(
      (nextState) => {
        setSelectedItemsState((previousState) => {
          const resolvedState =
            typeof nextState === 'function' ? nextState(previousState) : nextState;

          if (!maxSelections || maxSelections < 1) {
            return resolvedState;
          }

          return resolvedState.slice(-maxSelections);
        });
      },
      [maxSelections],
    );

    // select an item
    const selectItem = useCallback(
      (record?: GenericSearchItem) => {
        const recordId = getRecordRealId(record, resource);
        if (!record || !recordId) {
          console.warn('[selectItem] Missing record or id:', record);
          return;
        }
        setSelectedItems((prevSelect) => {
          const index = prevSelect.findIndex((item) => {
            const itemId = getRecordRealId(item, resource);
            return itemId === recordId;
          });
          // item exists, move it to the end so we can
          // use last element as most recent selection
          // for specific actions in context menus and css
          if (index !== -1) {
            const updated = [...prevSelect]; // copy
            const [existing] = updated.splice(index, 1); // remove
            updated.push(existing); // add to end
            return updated;
          }
          // was not selected, add item to end
          return [...prevSelect, record];
        });
      },
      [resource, setSelectedItems],
    );

    // select an item by string id, try to find in the map, if not found will warn and return
    const selectById = useCallback(
      (id?: string) => {
        if (!id) {
          console.warn('[selectById] Missing ID param:', id);
          return;
        }
        const record = recordMap.get(id);
        if (!record) {
          console.warn('[selectById] No record found in map with ID:', id);
          return;
        }
        // call helper to add to the end of the selected items array
        selectItem(record);
      },
      [recordMap, selectItem],
    );

    // deselect an item by id and remove from state
    const deselectById = useCallback(
      (id?: string) => {
        if (!id) {
          console.warn('[deselectById] Missing id param:', id);
          return;
        }
        setSelectedItems((prevSelect) => {
          const isSelected = prevSelect.some((item) => {
            const itemId = getRecordRealId(item, resource);
            if (!itemId) {
              return false;
            }
            return Boolean(itemId === id);
          });
          // id was not in selections, return prev state as is
          if (!isSelected) {
            return prevSelect;
          }

          const removed = prevSelect.filter((item) => {
            const itemId = getRecordRealId(item, resource);
            if (!itemId) {
              return false;
            }
            return Boolean(itemId !== id);
          });

          return removed;
        });
      },
      [resource, setSelectedItems],
    );

    // select or deselect if already selected
    const toggleSelectItem = useCallback(
      (record?: GenericSearchItem) => {
        const recordId = getRecordRealId(record, resource);
        if (!record || !recordId) {
          console.warn('[toggleSelectItem] Missing record or id:', record);
          return;
        }
        // check if selected, remove if present, add if missing
        setSelectedItems((prevSelect) => {
          const isSelected = prevSelect.some((item) => {
            const itemId = getRecordRealId(item, resource);
            if (!itemId) {
              return false;
            }
            return Boolean(itemId === recordId);
          });
          // Item was selected, remove it from the selected items
          if (isSelected) {
            const filtered = prevSelect.filter((item) => {
              const itemId = getRecordRealId(item, resource);
              if (!itemId) {
                return false;
              }
              return Boolean(itemId !== recordId);
            });
            return filtered;
          }
          // was not selected, add to the end of the selected items array
          const added = [...prevSelect, record];
          return added;
        });
      },
      [resource, setSelectedItems],
    );

    // clear selected items
    const clearSelectedItems = useCallback(() => {
      setSelectedItems(emptyArr);
    }, [setSelectedItems]);

    // deduped string[] ids for MUI DataGrid rowSelectionModel specific
    const selectedItemIds: GridRowSelectionModel = useMemo(() => {
      const allIds = selectedItems.map((item) => getRecordRealId(item, resource));
      const filteredIds = allIds.filter((id) => Boolean(id));
      const uniqueIds = new Set<string>(filteredIds);
      return {
        type: 'include',
        ids: uniqueIds,
      };
    }, [resource, selectedItems]);

    // track last selected item for certain context actions and css
    // ensure setItem / selectById / etc... context methods are setting
    // the newly selected item to the end of the array for this to be accurate
    const lastIndexSelection = useMemo(() => {
      if (
        !selectedItems ||
        !Array.isArray(selectedItems) ||
        selectedItems?.length === 0
      ) {
        return undefined;
      }
      return selectedItems[selectedItems.length - 1];
    }, [selectedItems]);

    // views with selection capability (eg checkboxes)
    // usage is optional within search views as a helper
    const hasSelectionEnabled = useMemo(
      () => enableSelection === true || checkSelectionEnabled(resource),
      [enableSelection, resource],
    );

    const hasMiniMenuEnabled = useMemo(
      () => checkMiniMenuEnabled(resource, sessionConfig),
      [resource, sessionConfig],
    );

    const contextValue: SearchItemsContextType<GenericSearchItem> = useMemo(() => {
      return {
        recordMap,
        cleanData,
        // Selection handling
        hasMiniMenuEnabled,
        hasSelectionEnabled,
        selectedItemIds,
        selectItem,
        selectById,
        deselectById,
        toggleSelectItem,
        clearSelectedItems,
        // base states as needed
        setSelectedItems,
        selectedItems,
        lastIndexSelection,
        // react-admin useListContext context
        data,
        hasNextPage,
        hasPreviousPage,
        isFetching,
        isLoading,
        page,
        perPage,
        refetch,
        error,
        total,
        // react-admin useInfinitePaginationContext context
        fetchNextPage,
        fetchPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        infHasNextPage,
        infHasPrevPage,
        // Mini menu configuration
        miniMenuConfig,
        miniMenuHandlers,
      };
    }, [
      recordMap,
      cleanData,
      hasMiniMenuEnabled,
      hasSelectionEnabled,
      selectedItems,
      lastIndexSelection,
      selectedItemIds,
      selectItem,
      selectById,
      deselectById,
      toggleSelectItem,
      clearSelectedItems,
      data,
      hasNextPage,
      hasPreviousPage,
      isFetching,
      isLoading,
      page,
      perPage,
      refetch,
      error,
      total,
      fetchNextPage,
      fetchPreviousPage,
      isFetchingNextPage,
      isFetchingPreviousPage,
      infHasNextPage,
      infHasPrevPage,
      miniMenuConfig,
      miniMenuHandlers,
      setSelectedItems,
    ]);

    return (
      <SearchItemsContext.Provider value={contextValue}>
        {children}
      </SearchItemsContext.Provider>
    );
  },
);
SearchItemsProvider.displayName = 'SearchItemsProvider';
